import { useState, useRef, type KeyboardEvent, type ChangeEvent } from 'react';
import { FilePreview, type PendingFile } from './FilePreview';
import { MorphingButton, type ButtonState } from './MorphingButton';
import { FILE_ACCEPT_STRING } from '../config/fileUpload';
import { t } from '../utils/translations';

interface InputAreaProps {
  onSend: (message: string) => void;
  isConnected: boolean;
  pendingFiles: PendingFile[];
  onFileRemove: (id: string) => void;
  onFilesSelected?: (files: File[]) => void;
  language?: string;
}

export function InputArea({
  onSend,
  isConnected,
  pendingFiles,
  onFileRemove,
  onFilesSelected,
  language = 'en',
}: InputAreaProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if all files are uploaded (not still uploading or errored)
  const allFilesReady = pendingFiles.every((f) => f.status === 'uploaded');
  const hasFiles = pendingFiles.length > 0;
  const hasText = message.trim().length > 0;

  // Can send if connected, has text, and files are ready (if any)
  const canSend = isConnected && hasText && (!hasFiles || allFilesReady);

  // Button state based on textarea content
  const buttonState: ButtonState = hasText ? 'send' : 'attach';

  const handleSendMessage = () => {
    if (canSend) {
      onSend(message.trim());
      setMessage('');
      // Reset height
      if (textareaRef.current) {
        textareaRef.current.style.height = '44px';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);

    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  };

  const handleButtonClick = () => {
    if (buttonState === 'attach') {
      // Open file picker
      fileInputRef.current?.click();
    } else {
      // Send message
      handleSendMessage();
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && onFilesSelected) {
      onFilesSelected(files);
    }
    // Reset input
    e.target.value = '';
  };

  return (
    <div className="border-t p-3 pb-4 relative">
      {pendingFiles.length > 0 && (
        <div className="mb-2">
          <FilePreview files={pendingFiles} onRemove={onFileRemove} />
        </div>
      )}

      {/* Connection warning */}
      {!isConnected && (
        <div className="mb-2 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
          Connecting... Your message will be sent when connected.
        </div>
      )}

      {/* Input wrapper with relative positioning for floating button */}
      <div className="relative">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder={t('input.placeholder', language)}
          rows={1}
          className="w-full px-3 py-2.5 pr-12 text-sm resize-none overflow-hidden border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          style={{ maxHeight: '120px', height: '44px', lineHeight: '1.5' }}
        />

        {/* Morphing floating button */}
        <MorphingButton
          state={buttonState}
          onClick={handleButtonClick}
          disabled={buttonState === 'send' && !canSend}
        />

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileChange}
          className="hidden"
          accept={FILE_ACCEPT_STRING}
        />
      </div>
    </div>
  );
}
